import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FlaskConical, Plus, RefreshCw, Search, Truck } from "lucide-react";
import { humanizeCode } from "@/lib/utils";
import type { SampleRecord, SampleStatus, SampleSummary } from "@/types";
import { SampleStatusBadge, SAMPLE_STATUS_LABELS } from "@/components/samples/SampleStatusBadge";
import { SampleActions } from "@/components/samples/SampleActions";
import { RecordPhotosButton } from "@/components/attachments/RecordPhotosButton";
import { SampleFormDialog } from "@/components/samples/SampleFormDialog";

const formatDate = (d?: string | null) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "-");

/** Filter chips: "Open" is everything still moving; the rest are single statuses */
const FILTERS: Array<{ key: string; label: string; status: string }> = [
  { key: "OPEN", label: "Open", status: "REQUESTED,IN_PROGRESS,DISPATCHED,REVISION" },
  { key: "DISPATCHED", label: SAMPLE_STATUS_LABELS.DISPATCHED, status: "DISPATCHED" },
  { key: "APPROVED", label: SAMPLE_STATUS_LABELS.APPROVED, status: "APPROVED" },
  { key: "REJECTED", label: SAMPLE_STATUS_LABELS.REJECTED, status: "REJECTED,CANCELLED" },
  { key: "ALL", label: "All", status: "" },
];

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold tracking-tight mt-1">{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

export function SamplesPage() {
  const { error } = useToast();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("sample.create");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("OPEN");
  const [summary, setSummary] = useState<SampleSummary | null>(null);
  const [dialog, setDialog] = useState<SampleRecord | "new" | null>(null);

  const list = usePagedList<SampleRecord>({
    deps: [search, filter],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams({ page: String(page), page_size: "30" });
      if (search.trim()) qs.set("search", search.trim());
      const status = FILTERS.find((f) => f.key === filter)?.status;
      if (status) qs.set("status", status);
      try {
        return unwrapPage<SampleRecord>(await api.get(`/api/Sample/List?${qs}`));
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load samples");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });

  const loadSummary = () =>
    api
      .get("/api/Sample/Summary")
      .then((r) => setSummary(unwrapObject<SampleSummary>(r)))
      .catch(() => setSummary(null));
  useEffect(() => {
    loadSummary();
  }, []);

  const refresh = () => {
    list.reload();
    loadSummary();
  };
  const pct = (n: number | null | undefined) => (n == null ? "-" : `${n}%`);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Samples</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Swatches, lab dips, strike-offs and PP samples sent to buyers for approval</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh} disabled={list.loading} className="h-9 gap-1.5">
            <RefreshCw className={`size-3.5 ${list.loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {canCreate && (
            <Button size="sm" onClick={() => setDialog("new")} className="h-9 gap-1.5 shadow-sm">
              <Plus className="size-4" />
              Request sample
            </Button>
          )}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Tile label="Open samples" value={String(summary.open)} hint={`${summary.by_status.DISPATCHED || 0} with buyers now`} />
          <Tile label="Approval rate" value={pct(summary.approval_rate_pct)} hint="Approved of all decided" />
          <Tile label="Days to approval" value={summary.avg_days_to_approval == null ? "-" : String(summary.avg_days_to_approval)} hint="Request to approval, average" />
          <Tile
            label="Sample to order"
            value={pct(summary.sample_to_order_pct)}
            hint={`${summary.deals_won_after_approval} of ${summary.deals_with_approved_sample} deals with an approved sample won`}
          />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
          <Input placeholder="Search by number, article, buyer or AWB..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-9 text-sm" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${filter === f.key ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-border"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {list.loading && list.rows.length === 0 ? (
        <TableSkeleton rows={6} columns={6} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sample</TableHead>
                <TableHead>Buyer / deal</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.rows.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>
                    <div className="flex items-start gap-2.5">
                      <div className="size-8 rounded-lg bg-violet-500/10 text-violet-600 dark:text-violet-400 flex items-center justify-center shrink-0">
                        <FlaskConical className="size-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm">{s.product_name}</div>
                        <div className="text-[11px] text-muted-foreground">
                          <code className="font-mono">{s.sample_number}</code>
                          {s.variant_name ? ` · ${s.variant_name}` : ""}
                          {s.quantity ? ` · ${Number(s.quantity)} ${s.unit || ""}` : ""}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div>{s.account?.name || "-"}</div>
                    {s.deal && (
                      <Link to={`/deals/${s.deal.id}`} className="text-xs text-primary hover:underline">
                        {s.deal.name}
                      </Link>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{humanizeCode(s.sample_type)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    <div>Asked {formatDate(s.requested_on)}</div>
                    {s.needed_by && <div>Needed {formatDate(s.needed_by)}</div>}
                    {s.dispatched_on && (
                      <div className="flex items-center gap-1 text-foreground">
                        <Truck className="size-3" /> {s.courier} {s.awb_number ? `· ${s.awb_number}` : ""}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <SampleStatusBadge status={s.sample_status as SampleStatus} round={s.round} />
                    {s.buyer_comments && ["REJECTED", "REVISION", "APPROVED"].includes(s.sample_status) && (
                      <div className="text-[11px] text-muted-foreground mt-1 max-w-[220px] break-words">{s.buyer_comments}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end items-center gap-1.5 flex-wrap">
                      <RecordPhotosButton type="SAMPLE" recordId={s.id} count={s.photo_count} onChanged={refresh} title={`${s.sample_number} · ${s.product_name}`} compact />
                      <SampleActions sample={s} onChanged={refresh} onEdit={(x) => setDialog(x)} compact />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {list.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground text-sm">
                    No samples here.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </div>
      )}

      <SampleFormDialog target={dialog} onClose={() => setDialog(null)} onSaved={refresh} />
    </div>
  );
}
